import React, { useState, useEffect } from "react";

interface LLMProviderInfo {
  name: string;
  api_key_env: string;
  models: Array<{
    id: string;
    name: string;
    recommended?: boolean;
  }>;
}

interface LLMProviders {
  gemini: LLMProviderInfo;
  openrouter: LLMProviderInfo;
  openai: LLMProviderInfo;
  anthropic: LLMProviderInfo;
}

interface AgentLLMSettings {
  agent_name: string;
  llm_provider: string;
  llm_model: string;
  llm_api_key_env: string;
  llm_temperature: number;
  llm_max_tokens: number;
  llm_config_json: Record<string, any>;
  qualifier_ai_prompt: string;
  updated_at: string;
}

export const AgentLLMConfig: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AgentLLMSettings | null>(null);
  const [providers, setProviders] = useState<LLMProviders | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Local state for editing
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [temperature, setTemperature] = useState(0.1);
  const [maxTokens, setMaxTokens] = useState(500);
  const [aiPrompt, setAiPrompt] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load current settings
      const response = await fetch('http://localhost:8081/admin/agents/settings/qualifier_agent');
      const data = await response.json();

      if (data.success && data.settings) {
        setSettings(data.settings);
        setSelectedProvider(data.settings.llm_provider || 'gemini');
        setSelectedModel(data.settings.llm_model || 'gemini-2.5-flash-lite-preview-09-2025');
        setTemperature(parseFloat(data.settings.llm_temperature) || 0.1);
        setMaxTokens(data.settings.llm_max_tokens || 500);
        setAiPrompt(data.settings.qualifier_ai_prompt || '');
      }

      // Load available providers
      const providersResponse = await fetch('http://localhost:8081/admin/agents/llm-providers');
      const providersData = await providersResponse.json();

      if (providersData.success && providersData.providers) {
        setProviders(providersData.providers);
      }
    } catch (error) {
      console.error("Failed to load LLM configuration:", error);
      showMessage('error', 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleProviderChange = (provider: string) => {
    setSelectedProvider(provider);

    // Auto-select first recommended model for this provider
    if (providers && providers[provider as keyof LLMProviders]) {
      const providerModels = providers[provider as keyof LLMProviders].models;
      const recommendedModel = providerModels.find(m => m.recommended);
      setSelectedModel(recommendedModel ? recommendedModel.id : providerModels[0].id);
    }
  };

  const handleSaveLLMConfig = async () => {
    try {
      setSaving(true);

      const response = await fetch('http://localhost:8081/admin/agents/settings/qualifier_agent/llm', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          llm_provider: selectedProvider,
          llm_model: selectedModel,
          llm_temperature: temperature,
          llm_max_tokens: maxTokens,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', 'LLM configuration updated successfully');
        await loadData(); // Reload to get updated settings
      } else {
        showMessage('error', data.error || 'Failed to update LLM configuration');
      }
    } catch (error) {
      console.error("Failed to update LLM configuration:", error);
      showMessage('error', 'Failed to update LLM configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      setSaving(true);

      const response = await fetch('http://localhost:8081/admin/agents/settings/qualifier_agent/prompt', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', 'AI prompt updated successfully');
        await loadData();
      } else {
        showMessage('error', data.error || 'Failed to update prompt');
      }
    } catch (error) {
      console.error("Failed to update prompt:", error);
      showMessage('error', 'Failed to update prompt');
    } finally {
      setSaving(false);
    }
  };

  const getAvailableModels = () => {
    if (!providers || !selectedProvider) return [];
    const provider = providers[selectedProvider as keyof LLMProviders];
    return provider ? provider.models : [];
  };

  const hasLLMChanges = settings && (
    selectedProvider !== settings.llm_provider ||
    selectedModel !== settings.llm_model ||
    temperature !== parseFloat(settings.llm_temperature?.toString() || '0.1') ||
    maxTokens !== settings.llm_max_tokens
  );

  const hasPromptChanges = settings && aiPrompt !== (settings.qualifier_ai_prompt || '');

  if (loading) {
    return (
      <div className="llm-config-container">
        <div className="loading-state">Loading LLM configuration...</div>
      </div>
    );
  }

  return (
    <div className="llm-config-container">
      <div className="llm-config-header">
        <h2>🤖 AI Agent LLM Configuration</h2>
        <p className="llm-config-description">
          Configure the AI model and prompt for the Lead Qualifier Agent
        </p>
      </div>

      {message && (
        <div className={`message-banner ${message.type}`}>
          {message.type === 'success' ? '✅ ' : '❌ '}
          {message.text}
        </div>
      )}

      <div className="llm-config-sections">
        {/* LLM Model Configuration Section */}
        <div className="config-section">
          <div className="section-header">
            <h3>Model Configuration</h3>
            {settings && (
              <span className="last-updated">
                Last updated: {new Date(settings.updated_at).toLocaleString()}
              </span>
            )}
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="provider-select">LLM Provider</label>
              <select
                id="provider-select"
                value={selectedProvider}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="form-control"
              >
                {providers && Object.entries(providers).map(([key, provider]) => (
                  <option key={key} value={key}>
                    {provider.name}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Select the AI provider for lead qualification
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="model-select">Model</label>
              <select
                id="model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="form-control"
              >
                {getAvailableModels().map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} {model.recommended ? '⭐' : ''}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                ⭐ indicates recommended models for this task
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="temperature-input">
                Temperature: {temperature.toFixed(2)}
              </label>
              <input
                id="temperature-input"
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="form-control range-input"
              />
              <small className="form-hint">
                Lower = more focused, Higher = more creative (0.0 - 2.0)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="max-tokens-input">Max Tokens</label>
              <input
                id="max-tokens-input"
                type="number"
                min="100"
                max="4000"
                step="100"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                className="form-control"
              />
              <small className="form-hint">
                Maximum response length (100 - 4000)
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={handleSaveLLMConfig}
              disabled={!hasLLMChanges || saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Model Configuration'}
            </button>
            {hasLLMChanges && (
              <span className="changes-indicator">● Unsaved changes</span>
            )}
          </div>
        </div>

        {/* AI Prompt Configuration Section */}
        <div className="config-section">
          <div className="section-header">
            <h3>AI System Prompt</h3>
            <span className="section-hint">
              Define how the AI analyzes conversations for lead qualification
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="ai-prompt-textarea">System Prompt</label>
            <textarea
              id="ai-prompt-textarea"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={15}
              className="form-control prompt-textarea"
              placeholder="Enter the AI system prompt for lead qualification..."
            />
            <small className="form-hint">
              Use {'{conversationText}'} as a placeholder for the chat messages.
              The AI will analyze the conversation and return qualification results.
            </small>
          </div>

          <div className="form-actions">
            <button
              onClick={handleSavePrompt}
              disabled={!hasPromptChanges || saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save AI Prompt'}
            </button>
            {hasPromptChanges && (
              <span className="changes-indicator">● Unsaved changes</span>
            )}
          </div>
        </div>

        {/* Current Configuration Display */}
        {settings && (
          <div className="config-section current-config">
            <h3>📊 Current Active Configuration</h3>
            <div className="config-display">
              <div className="config-row">
                <span className="config-label">Provider:</span>
                <span className="config-value">{settings.llm_provider}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Model:</span>
                <span className="config-value">{settings.llm_model}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Temperature:</span>
                <span className="config-value">{settings.llm_temperature}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Max Tokens:</span>
                <span className="config-value">{settings.llm_max_tokens}</span>
              </div>
              <div className="config-row">
                <span className="config-label">API Key:</span>
                <span className="config-value">
                  env.{settings.llm_api_key_env}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .llm-config-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }

        .llm-config-header {
          margin-bottom: 32px;
        }

        .llm-config-header h2 {
          font-size: 28px;
          font-weight: bold;
          margin: 0 0 8px 0;
          color: #1a202c;
        }

        .llm-config-description {
          font-size: 16px;
          color: #718096;
          margin: 0;
        }

        .message-banner {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-weight: 500;
        }

        .message-banner.success {
          background-color: #c6f6d5;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .message-banner.error {
          background-color: #fed7d7;
          color: #742a2a;
          border: 1px solid #fc8181;
        }

        .llm-config-sections {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .config-section {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e2e8f0;
        }

        .section-header h3 {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          color: #2d3748;
        }

        .section-hint {
          font-size: 14px;
          color: #718096;
        }

        .last-updated {
          font-size: 13px;
          color: #a0aec0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 8px;
        }

        .form-control {
          padding: 10px 12px;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .form-control:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .prompt-textarea {
          font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
          font-size: 13px;
          line-height: 1.6;
          resize: vertical;
        }

        .range-input {
          padding: 0;
          height: 6px;
        }

        .form-hint {
          font-size: 12px;
          color: #718096;
          margin-top: 6px;
        }

        .form-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 20px;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background-color: #4299e1;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #3182ce;
        }

        .btn-primary:disabled {
          background-color: #cbd5e0;
          cursor: not-allowed;
        }

        .changes-indicator {
          font-size: 13px;
          color: #f56565;
          font-weight: 500;
        }

        .current-config {
          background: #f7fafc;
        }

        .current-config h3 {
          font-size: 18px;
          margin-bottom: 16px;
        }

        .config-display {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .config-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }

        .config-label {
          font-weight: 600;
          color: #4a5568;
          font-size: 14px;
        }

        .config-value {
          font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
          color: #2d3748;
          font-size: 13px;
        }

        .loading-state {
          text-align: center;
          padding: 48px;
          color: #718096;
          font-size: 16px;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};
